// SPDX-License-Identifier: MIT
// Desktop ↔ cloud sync (step 5). The desktop is local-first ; this is the
// OPT-IN bridge so a signed-in user's local projects appear on the web (and
// vice-versa). v1 syncs the PROJECT entity only — versions / conversations /
// attachments / edits are a deliberate follow-up (they need richer mapping +
// conflict handling). Idempotent via a persisted localId→cloudId map: a row
// already in the map is never re-created, so repeated syncs are no-ops.
//
// Dependency-injected (local repo / cloud repo / map store) so the orchestration
// is unit-tested without Tauri or Supabase. The wiring (real desktop stores +
// Supabase api + Rust syncmap) lives in run-cloud-sync.ts.
import type { ProjectRow } from '../projects/api';
import { projectRowToMagnifyResult } from '../projects/api';
import type { MagnifyResult } from '../auth/magnify';
import type { Locale } from '../i18n/detect';

export interface SyncLocalRepo {
  listProjects(): Promise<ProjectRow[]>;
  saveProject(result: MagnifyResult, idea: string, locale: Locale): Promise<ProjectRow>;
}
export type SyncCloudRepo = SyncLocalRepo;

export interface SyncMapStore {
  load(): Promise<Record<string, string>>; // localId → cloudId
  save(map: Record<string, string>): Promise<void>;
}

export interface SyncResult {
  pushed: number;
  pulled: number;
  skipped: number;
}

// PURE: which local versions are absent from the cloud (by version_number).
// Mirror of the pull-side dedup ; used by run-cloud-sync.pushVersionsFor.
export function versionsToPush<T extends { version_number: number }>(
  localVersions: T[],
  cloudVersionNumbers: number[],
): T[] {
  const have = new Set(cloudVersionNumbers);
  return localVersions.filter((v) => !have.has(v.version_number));
}

export async function syncProjects(
  local: SyncLocalRepo,
  cloud: SyncCloudRepo,
  mapStore: SyncMapStore,
): Promise<SyncResult> {
  const map = await mapStore.load();
  const knownCloudIds = new Set(Object.values(map));
  let pushed = 0, pulled = 0, skipped = 0;

  // PUSH — local projects not yet mapped get created in the cloud.
  for (const row of await local.listProjects()) {
    if (map[row.id]) { skipped++; continue; }
    const created = await cloud.saveProject(projectRowToMagnifyResult(row), row.idea, row.locale);
    map[row.id] = created.id;
    knownCloudIds.add(created.id);
    pushed++;
  }

  // PULL — cloud projects we've never seen (created on the web) get created
  // locally. Just-pushed ids are already in knownCloudIds, so they don't echo.
  for (const c of await cloud.listProjects()) {
    if (knownCloudIds.has(c.id)) continue;
    const created = await local.saveProject(projectRowToMagnifyResult(c), c.idea, c.locale);
    map[created.id] = c.id;
    knownCloudIds.add(c.id);
    pulled++;
  }

  await mapStore.save(map);
  return { pushed, pulled, skipped };
}
